import { Injectable, inject, signal } from '@angular/core';
import { AuthService } from './auth.service';
import { SupabaseService } from './supabase.service';
import { LoggerService } from './logger.service';
import { FeatureFlagService } from './feature-flag.service';
import { P } from '../constants/permissions';

export interface Permission {
  name: string;
  resource: string;
  action: string;
  scope?: string;
  description: string;
  module: string;
}

export interface Role {
  slug: string;
  name: string;
  description: string;
  permissions: string[];
}

@Injectable({
  providedIn: 'root',
})
export class RbacService {
  private auth = inject(AuthService);
  private supabase = inject(SupabaseService);
  private logger = inject(LoggerService);
  private featureFlags = inject(FeatureFlagService);

  // Cache for permissions and roles
  private _userPermissions = signal<string[]>([]);
  private _allPermissions = signal<Permission[]>([]);
  private _allRoles = signal<Role[]>([]);

  userPermissions = this._userPermissions.asReadonly();
  allPermissions = this._allPermissions.asReadonly();
  allRoles = this._allRoles.asReadonly();

  constructor() {
    // Note: Permissions are now loaded explicitly in AuthResolver
    // No effects needed - simpler and more predictable
  }

  // Core permission checking
  hasPermission(permission: string): boolean {
    if (this.auth.isSuperAdmin()) {
      return true;
    }

    // Organization owners have all permissions except system-level ones
    if (this.auth.isOrganizationOwner() && !permission.startsWith('system:')) {
      return true;
    }

    return this._userPermissions().includes(permission);
  }

  hasAnyPermission(permissions: string[]): boolean {
    if (this.auth.isSuperAdmin()) {
      return true;
    }

    // Owners have any non-system permission
    if (
      this.auth.isOrganizationOwner() &&
      permissions.some((perm) => !perm.startsWith('system:'))
    ) {
      return true;
    }

    const userPerms = this._userPermissions();
    return permissions.some((perm) => userPerms.includes(perm));
  }

  hasAllPermissions(permissions: string[]): boolean {
    if (this.auth.isSuperAdmin()) {
      return true;
    }

    // Owners have all non-system permissions
    if (
      this.auth.isOrganizationOwner() &&
      permissions.every((perm) => !perm.startsWith('system:'))
    ) {
      return true;
    }

    const userPerms = this._userPermissions();
    return permissions.every((perm) => userPerms.includes(perm));
  }

  // Specific permission checks
  canCreateCards(): boolean {
    return this.hasPermission('card:create');
  }

  canReadAllCards(): boolean {
    return this.hasPermission('card:read:all');
  }

  canUpdateAllCards(): boolean {
    return this.hasPermission('card:update:all');
  }

  canDeleteAllCards(): boolean {
    return this.hasPermission('card:delete:all');
  }

  canPublishCards(): boolean {
    return this.hasPermission('card:publish');
  }

  // :members scope convenience methods
  canReadMemberCards(): boolean {
    return this.hasPermission('card:read:members');
  }

  canUpdateMemberCards(): boolean {
    return this.hasPermission('card:update:members');
  }

  canDeleteMemberCards(): boolean {
    return this.hasPermission('card:delete:members');
  }

  canReadMemberContacts(): boolean {
    return this.hasPermission('contact:read:members');
  }

  canUpdateMemberContacts(): boolean {
    return this.hasPermission('contact:update:members');
  }

  canDeleteMemberContacts(): boolean {
    return this.hasPermission('contact:delete:members');
  }

  canViewMemberMedia(): boolean {
    return this.hasPermission('media:view:members');
  }

  // Hierarchical permission helpers (any level of access)
  canReadCards(): boolean {
    return this.hasAnyPermission(['card:read:all', 'card:read:members', 'card:read:own']);
  }

  canUpdateCards(): boolean {
    return this.hasAnyPermission(['card:update:all', 'card:update:members', 'card:update:own']);
  }

  canDeleteCards(): boolean {
    return this.hasAnyPermission(['card:delete:all', 'card:delete:members', 'card:delete:own']);
  }

  canReadContacts(): boolean {
    return this.hasAnyPermission(['contact:read:all', 'contact:read:members', 'contact:read:own']);
  }

  canUpdateContacts(): boolean {
    return this.hasAnyPermission([
      'contact:update:all',
      'contact:update:members',
      'contact:update:own',
    ]);
  }

  canDeleteContacts(): boolean {
    return this.hasAnyPermission([
      'contact:delete:all',
      'contact:delete:members',
      'contact:delete:own',
    ]);
  }

  canViewMedia(): boolean {
    return this.hasAnyPermission(['media:view:all', 'media:view:members', 'media:view:own']);
  }

  canManageTeam(): boolean {
    return this.hasPermission(P.MEMBER_MANAGE);
  }

  canInviteMembers(): boolean {
    return this.hasPermission(P.MEMBER_INVITE);
  }

  canViewTeam(): boolean {
    return this.hasPermission(P.MEMBER_VIEW);
  }

  canManageBusiness(): boolean {
    return this.auth.isOrganizationOwner() || this.hasPermission(P.BUSINESS_UPDATE);
  }

  canManageContacts(): boolean {
    return this.canReadContacts() || this.canUpdateContacts();
  }

  canDeleteMedia(): boolean {
    return this.hasPermission(P.MEDIA_DELETE);
  }

  // Branch permissions
  canReadBranches(): boolean {
    return this.hasPermission(P.BRANCH_READ);
  }

  canCreateBranches(): boolean {
    return this.hasPermission(P.BRANCH_CREATE);
  }

  canUpdateBranches(): boolean {
    return this.hasPermission(P.BRANCH_UPDATE);
  }

  canDeleteBranches(): boolean {
    return this.hasPermission(P.BRANCH_DELETE);
  }

  canManageBranches(): boolean {
    return (
      this.auth.isOrganizationOwner() ||
      this.hasAnyPermission([P.BRANCH_CREATE, P.BRANCH_UPDATE, P.BRANCH_DELETE])
    );
  }

  // Business card duplication permission checks
  canDuplicateCards(): boolean {
    // Can duplicate if can create cards and can read at least own cards
    return this.canCreateCards() && this.canReadCards();
  }

  canAssignCardsToOthers(): boolean {
    // Can assign to others if has update permissions for members or all
    return this.hasAnyPermission(['card:update:all', 'card:update:members']);
  }

  canAssignCardsToAllUsers(): boolean {
    // Can assign to all users in business (business owner permission)
    return this.hasPermission('card:update:all');
  }

  canAssignCardsToMembers(): boolean {
    // Can assign to cashier/kitchen role members (business manager permission)
    return this.hasPermission('card:update:members');
  }

  canAssignCardsAcrossBusinesses(): boolean {
    // Super admin can assign across businesses
    return this.auth.isSuperAdmin();
  }

  // User creation permission checks
  canCreateUsers(): boolean {
    return this.hasPermission(P.MEMBER_INVITE);
  }

  canCreateManagers(): boolean {
    // Only business owners and super admins can create managers
    return this.auth.isSuperAdmin() || this.hasPermission(P.MEMBER_MANAGE);
  }

  canSearchAllUsers(): boolean {
    // Super admin can search across all businesses
    return this.auth.isSuperAdmin();
  }

  canSearchAllBusinesses(): boolean {
    // Only super admin can search businesses
    return this.auth.isSuperAdmin();
  }

  getCreatableRoles(): string[] {
    // ==========================================================================
    // SECURITY: Super Admin and Owner can ONLY be assigned via direct DB access.
    // Admin requires member:deactivate (Owner-tier).
    // Manager requires member:manage (Admin-tier or above).
    // Cashier/Kitchen/Stock Clerk require member:invite (Manager-tier or above).
    // Kitchen is filtered out for non-restaurant business types — see filterByBusinessType.
    // ==========================================================================
    if (this.auth.isSuperAdmin()) {
      return this.filterByBusinessType(['admin', 'operations_manager', 'manager', 'cashier', 'kitchen', 'stock_clerk']);
    }

    // Owner-tier (member:deactivate): full assignable set including Admin and Operations Manager
    if (this.hasPermission(P.MEMBER_DEACTIVATE)) {
      return this.filterByBusinessType(['admin', 'operations_manager', 'manager', 'cashier', 'kitchen', 'stock_clerk']);
    }

    // Admin-tier (member:manage, no member:deactivate): can assign Manager and below
    if (this.hasPermission(P.MEMBER_MANAGE)) {
      return this.filterByBusinessType(['manager', 'cashier', 'kitchen', 'stock_clerk']);
    }

    // Manager-tier (member:invite only): can assign Cashier/Kitchen/Stock Clerk
    if (this.hasPermission(P.MEMBER_INVITE)) {
      return this.filterByBusinessType(['cashier', 'kitchen', 'stock_clerk']);
    }

    return [];
  }

  // Drop roles that don't apply to the current org context:
  //   - Kitchen: requires a restaurant context. Multi-business-type orgs (e.g., Wayne
  //     Enterprises: org-level 'general' with a 'restaurant' branch) are allowed
  //     because they have a real kitchen somewhere. The SECURITY DEFINER RPC mirrors
  //     this and additionally verifies a restaurant branch exists.
  //   - Stock Clerk: requires the inventory feature to be enabled — the role exists
  //     solely to operate inventory, and there's nothing for them to do without it.
  //
  // Existing role holders in orgs that lose the qualifying context are intentionally
  // left in place; this only blocks new assignments and edits.
  private filterByBusinessType(roles: string[]): string[] {
    const isRestaurantContext =
      this.auth.businessType() === 'restaurant' ||
      this.featureFlags.multiBusinessTypeEnabled();
    const inventoryOn = this.featureFlags.inventoryEnabled();
    return roles.filter((r) => {
      if (r === 'kitchen' && !isRestaurantContext) return false;
      if (r === 'stock_clerk' && !inventoryOn) return false;
      return true;
    });
  }

  // Get roles that current user can edit for a specific target user
  getEditableRolesForUser(targetUserRole: string, targetUserId: string): string[] {
    const currentUserId = this.auth.userContext()?.id;
    const isSuperAdmin = this.auth.isSuperAdmin();
    const canManage = this.canManageTeam();
    const canDeactivate = this.hasPermission(P.MEMBER_DEACTIVATE);
    const canInvite = this.canInviteMembers();

    // Users cannot edit their own role
    if (targetUserId === currentUserId) {
      return [];
    }

    // Caller must hold at least one entry-level permission (or be super admin)
    if (!isSuperAdmin && !canManage && !canInvite) {
      return [];
    }

    if (isSuperAdmin) {
      return this.filterByBusinessType(['admin', 'owner', 'operations_manager', 'manager', 'cashier', 'kitchen', 'stock_clerk']);
    }

    // Owner-tier (member:deactivate): can edit anyone except other Owners
    if (canDeactivate) {
      if (targetUserRole === 'owner') {
        return [];
      }
      return this.filterByBusinessType(['admin', 'operations_manager', 'manager', 'cashier', 'kitchen', 'stock_clerk']);
    }

    // Admin-tier (member:manage, no member:deactivate): cannot edit Admin/Owner/Super Admin/Operations Manager
    if (canManage) {
      if (['admin', 'owner', 'super_admin', 'operations_manager'].includes(targetUserRole)) {
        return [];
      }
      return this.filterByBusinessType(['manager', 'cashier', 'kitchen', 'stock_clerk']);
    }

    // Manager-tier (member:invite only): can edit Cashier/Kitchen/Stock Clerk but not peers/Admin/Owner
    if (canInvite) {
      if (!['cashier', 'kitchen', 'stock_clerk'].includes(targetUserRole)) {
        return [];
      }
      return this.filterByBusinessType(['cashier', 'kitchen', 'stock_clerk']);
    }

    return [];
  }

  // Check if current user can edit a specific user's role
  canEditUserRole(targetUserRole: string, targetUserId: string): boolean {
    return this.getEditableRolesForUser(targetUserRole, targetUserId).length > 0;
  }

  // Check if current user can deactivate (or reactivate) a target user.
  // Only Owner / Super Admin tier may do this — Admin is intentionally blocked
  // from un-doing Owner's deactivations. Self-deactivation is always blocked.
  canDeactivateUser(targetUserId: string): boolean {
    if (targetUserId === this.auth.userContext()?.id) {
      return false;
    }
    return this.hasPermission(P.MEMBER_DEACTIVATE);
  }

  getBusinessScope(): 'own' | 'cross-business' {
    return this.auth.isSuperAdmin() ? 'cross-business' : 'own';
  }

  getUserSearchScope(): 'none' | 'business' | 'all' {
    if (!this.hasPermission(P.MEMBER_VIEW)) {
      return 'none';
    }

    if (this.auth.isSuperAdmin()) {
      return 'all';
    }

    return 'business';
  }

  canReassignUsers(): boolean {
    return this.hasPermission(P.MEMBER_MANAGE);
  }

  // Load user's permissions from JWT claims (with database fallback)
  async loadUserPermissions() {
    try {
      // Try to get permissions from JWT first
      const {
        data: { session },
      } = await this.supabase.client.auth.getSession();
      if (session?.access_token) {
        const payload = this.decodeJWT(session.access_token);
        this.logger.debug('JWT payload', payload);
        if (payload && 'permissions' in payload && Array.isArray(payload['permissions'])) {
          const permissions = payload['permissions'] as string[];
          this._userPermissions.set(permissions);
          this.logger.debug('Loaded permissions from JWT', permissions.length);
          return;
        }
      }

      // Fallback to database query if JWT doesn't have permissions
      const { data, error } = await this.supabase.client.rpc('get_user_permissions');

      if (error) {
        this.logger.error('Error loading user permissions from database', error);
        return;
      }

      this._userPermissions.set(data || []);
    } catch (error) {
      this.logger.error('Failed to load user permissions', error);
      this._userPermissions.set([]);
    }
  }

  // Helper method to decode JWT (same as AuthService)
  private decodeJWT(token: string): Record<string, unknown> | null {
    try {
      const base64Url = token.split('.')[1];
      const base64 = base64Url.replace(/-/g, '+').replace(/_/g, '/');
      const jsonPayload = decodeURIComponent(
        atob(base64)
          .split('')
          .map((c) => '%' + ('00' + c.charCodeAt(0).toString(16)).slice(-2))
          .join(''),
      );
      return JSON.parse(jsonPayload) as Record<string, unknown>;
    } catch (error) {
      this.logger.error('Error decoding JWT', error);
      return null;
    }
  }

  // Lazy load all available permissions (for admin UI only)
  async loadAllPermissions() {
    // Only load if not already loaded
    if (this._allPermissions().length > 0) {
      return;
    }

    try {
      const { data, error } = await this.supabase.client
        .from('permission')
        .select('*')
        .order('module', { ascending: true })
        .order('name', { ascending: true });

      if (error) {
        this.logger.error('Error loading all permissions', error);
        return;
      }

      this._allPermissions.set(data || []);
    } catch (error) {
      this.logger.error('Failed to load all permissions', error);
    }
  }

  // Lazy load all available roles (for admin UI only)
  async loadAllRoles() {
    // Only load if not already loaded
    if (this._allRoles().length > 0) {
      return;
    }

    try {
      const { data, error } = await this.supabase.client
        .from('v_role_summary')
        .select('*')
        .order('name', { ascending: true });

      if (error) {
        this.logger.error('Error loading all roles', error);
        return;
      }

      this._allRoles.set(data || []);
    } catch (error) {
      this.logger.error('Failed to load all roles', error);
    }
  }

  // Admin functions for role/permission management
  async assignRoleToUser(userId: string, roleSlug: string, organizationId?: string): Promise<void> {
    // Caller-tier gate: anyone who can manage OR invite may call. The RPC enforces
    // target-tier and assigned-slug restrictions (Manager → Cashier/Kitchen only,
    // Admin cannot promote to Admin, etc.).
    if (!(this.canManageTeam() || this.canInviteMembers() || this.auth.isSuperAdmin())) {
      throw new Error('Insufficient permissions to assign roles');
    }

    const params = {
      p_user_id: userId,
      p_role_slug: roleSlug,
      p_organization_id: organizationId || this.auth.organizationId(),
    };

    const { error } = await this.supabase.client.rpc('assign_role_to_user', params);

    if (error) {
      throw new Error(`Failed to assign role: ${error.message}`);
    }
  }

  async removeUserRole(userId: string, organizationId?: string): Promise<void> {
    // Deactivation requires Owner-tier (member:deactivate). Self-deactivate blocked.
    if (!this.canDeactivateUser(userId)) {
      throw new Error('Insufficient permissions to remove roles');
    }

    const { error } = await this.supabase.client.rpc('deactivate_user_from_org', {
      p_user_id: userId,
      p_organization_id: organizationId || this.auth.organizationId(),
    });

    if (error) {
      throw new Error(`Failed to remove role: ${error.message}`);
    }
  }

  // Get permission matrix for UI display
  async getPermissionMatrix(): Promise<Record<string, unknown>[]> {
    const { data, error } = await this.supabase.client
      .from('v_role_permissions_matrix')
      .select('*')
      .order('module', { ascending: true })
      .order('permission_name', { ascending: true });

    if (error) {
      throw new Error(`Failed to load permission matrix: ${error.message}`);
    }

    return data || [];
  }

  // Refresh permissions cache
  async refreshPermissions(): Promise<void> {
    await this.loadUserPermissions();
  }

  // =========================================================================
  // BRANCH ACCESS PERMISSIONS
  // =========================================================================

  /**
   * Check if user has access to all branches in their organization
   * Returns true if user has branch:access:all permission OR hasAllBranchAccess flag from JWT
   */
  canAccessAllBranches(): boolean {
    return this.auth.hasAllBranchAccess() || this.hasPermission(P.BRANCH_ACCESS_ALL);
  }

  /**
   * Check if user has access to a specific branch
   * @param branchId - The branch ID to check access for
   */
  canAccessBranch(branchId: string): boolean {
    if (this.canAccessAllBranches()) {
      return true;
    }
    return this.auth.branchIds().includes(branchId);
  }

  /**
   * Check if user can manage branch assignments (assign users to branches)
   * Requires member:manage permission
   */
  canManageBranchAssignments(): boolean {
    return this.hasPermission(P.MEMBER_MANAGE);
  }
}
